import { useCallback, useState } from 'react'
import {
  createPolicyTemplateGroup as createPolicyTemplateGroupApi,
  deletePolicyTemplateGroup as deletePolicyTemplateGroupApi,
  fetchPolicyTemplateGroupList as fetchPolicyTemplateGroupListApi,
  updatePolicyTemplateGroup as updatePolicyTemplateGroupApi,
} from '../crud/policyTemplateGroup.crud'

const mergePolicyTemplateGroups = (responses) => {
  const groupsById = new Map()

  responses
    .flatMap((response) => response?.data?.groups ?? [])
    .forEach((group) => {
      const existingGroup = groupsById.get(group.id)
      const hasTemplates =
        Object.prototype.hasOwnProperty.call(existingGroup ?? {}, 'templates') ||
        Object.prototype.hasOwnProperty.call(group, 'templates')
      const templatesById = new Map(
        [...(existingGroup?.templates ?? []), ...(group.templates ?? [])].map((template) => [template.id, template]),
      )

      groupsById.set(group.id, {
        ...existingGroup,
        ...group,
        ...(hasTemplates ? { templates: [...templatesById.values()] } : {}),
      })
    })

  return [...groupsById.values()]
}

const fetchPolicyTemplateGroupList = async ({ setData, setError, setLoading, policyType }) => {
  setLoading(true)
  setError(null)

  try {
    const results = await Promise.allSettled(
      (Array.isArray(policyType) ? policyType : [policyType]).map((type) =>
        fetchPolicyTemplateGroupListApi({ policyType: type }),
      ),
    )
    const responses = results.filter(({ status }) => status === 'fulfilled').map(({ value }) => value)

    results.filter(({ status }) => status === 'rejected').forEach(({ reason }) => console.error(reason))

    if (!responses.length) {
      throw results[0]?.reason
    }

    setData(mergePolicyTemplateGroups(responses))
  } catch (error) {
    console.error(error)
    setError(true)
  }

  setLoading(null)
}

const createPolicyTemplateGroup = async ({ setError, setLoading, params }) => {
  setLoading(true)
  setError(null)
  let success = true

  try {
    await createPolicyTemplateGroupApi(params)
  } catch (error) {
    success = false
    console.error(error)
    setError(true)
  }

  setLoading(null)

  return success
}

const updatePolicyTemplateGroup = async ({ setError, setLoading, params }) => {
  setLoading(true)
  setError(null)
  let success = true

  try {
    await updatePolicyTemplateGroupApi(params)
  } catch (error) {
    success = false
    console.error(error)
    setError(true)
  }

  setLoading(null)

  return success
}

const deletePolicyTemplateGroup = async ({ setError, setLoading, params }) => {
  setLoading(true)
  setError(null)
  let success = true

  try {
    await deletePolicyTemplateGroupApi(params)
  } catch (error) {
    success = false
    console.error(error)
    setError(true)
  }

  setLoading(null)

  return success
}

const usePolicyTemplateGroup = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [data, setData] = useState(null)

  const createPolicyTemplateGroupCb = useCallback(
    (params) =>
      createPolicyTemplateGroup({
        setError,
        setLoading,
        params,
      }),
    [],
  )

  const updatePolicyTemplateGroupCb = useCallback(
    (params) =>
      updatePolicyTemplateGroup({
        setError,
        setLoading,
        params,
      }),
    [],
  )

  const deletePolicyTemplateGroupCb = useCallback(
    (params) =>
      deletePolicyTemplateGroup({
        setError,
        setLoading,
        params,
      }),
    [],
  )

  const fetchPolicyTemplateGroupListCb = useCallback(
    ({ policyType }) =>
      fetchPolicyTemplateGroupList({
        setData,
        setError,
        setLoading,
        policyType,
      }),
    [],
  )

  const clearPolicyTemplateGroupError = useCallback(() => setError(null), [])

  return {
    loading,
    error,
    data,
    createPolicyTemplateGroup: createPolicyTemplateGroupCb,
    fetchPolicyTemplateGroupList: fetchPolicyTemplateGroupListCb,
    deletePolicyTemplateGroup: deletePolicyTemplateGroupCb,
    updatePolicyTemplateGroup: updatePolicyTemplateGroupCb,
    clearPolicyTemplateGroupError,
  }
}

export default usePolicyTemplateGroup
